import { Table, TableBody, TableFooter, TableHead, TableRow, Typography, Box, Link } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useTheme, styled } from '@mui/material/styles'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import { Link as NavLink } from 'react-router-dom'

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    width: '5em',
  },
}))

const StyledEmptyTableBodyCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    backgroundColor: theme.palette.action.disabledBackground,
    border: 0,
  },
}))

const StyledTableBodyCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    width: '5em',
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    borderBottom: `1px solid ${theme.palette.primary.main}`,
  },
}))

export default function FinalGrade({
  courseSessionEnrollmentGrades,
  markingPeriods,
  hasCourseSessionViewPermission,
  gpas,
}) {
  const { t } = useTranslation()
  const theme = useTheme()

  if (!(courseSessionEnrollmentGrades && markingPeriods)) return null

  const totalCredits = courseSessionEnrollmentGrades
    .reduce((acc, item) => {
      const credit = item.courseSession.credit ?? item.courseSession.course.credit
      return acc + (typeof credit === 'number' && !isNaN(credit) ? credit : 0)
    }, 0)
    .toFixed(2)

  return (
    <Box sx={{ margin: 2, overflowX: 'auto' }}>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableHeadCell style={{ width: '23em' }} className="font-weight-500">
              {t('Course Name')}
            </StyledTableHeadCell>
            <StyledTableHeadCell className="font-weight-500">
              {t('Credit')}
            </StyledTableHeadCell>
            {markingPeriods.map(mp => (
              <StyledTableHeadCell key={mp.id} className="font-weight-500">
                {mp.name}
              </StyledTableHeadCell>
            ))}
            <StyledTableHeadCell
              className="font-weight-500"
              style={{ borderLeft: `1px solid ${theme.palette.primary.main}` }}
            >
              {t('Final Grade')}
            </StyledTableHeadCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {courseSessionEnrollmentGrades.map(item => (
            <StyledTableRow key={item.id}>
              <StyledTableBodyCell sx={{ width: '23em' }}>
                {hasCourseSessionViewPermission ? (
                  <Link
                    component={NavLink}
                    to={`/settings/course-session/${item.courseSession.id}`}
                    underline="hover"
                    color="inherit"
                  >
                    {item.courseSession.course.name}
                  </Link>
                ) : (
                  item.courseSession.course.name
                )}
              </StyledTableBodyCell>
              <StyledTableBodyCell>
                {item.courseSession.credit ?? item.courseSession.course.credit}
              </StyledTableBodyCell>
              {markingPeriods.map(mp => {
                const mpGrade = item.markingPeriodGradeList?.find(el => el.markingPeriodId === mp.id)
                return mpGrade?.grade === undefined ? (
                  <StyledEmptyTableBodyCell key={mp.id} />
                ) : (
                  <StyledTableBodyCell key={mp.id}>
                    {mpGrade.gradeLetter}{' '}
                    {(mpGrade.grade || mpGrade.grade === 0) && `(${mpGrade.grade})`}
                  </StyledTableBodyCell>
                )
              })}
              <StyledTableBodyCell
                sx={{ borderLeft: `1px solid ${theme.palette.primary.main}` }}
              >
                {(item.grade || item.grade === 0) && item.finalGradeLetter}{' '}
                {(item.grade || item.grade === 0) ? `(${Math.round(item.grade)})` : ''}
              </StyledTableBodyCell>
            </StyledTableRow>
          ))}
        </TableBody>

        <TableFooter>
          <TableRow>
            <StyledTableBodyCell sx={{ borderBottom: 'none', width: '23em' }}>
              <Typography className="font-weight-500">{t('Average GPA and Credit Total')}:</Typography>
            </StyledTableBodyCell>
            <StyledTableBodyCell sx={{ borderBottom: 'none' }}>
              <Typography className="font-weight-500">{totalCredits}</Typography>
            </StyledTableBodyCell>
            {markingPeriods.map(mp => (
              <StyledTableBodyCell key={mp.id} sx={{ borderBottom: 'none' }} />
            ))}
            <StyledTableBodyCell
              sx={{
                borderBottom: 'none',
                borderLeft: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              <Typography className="font-weight-500">
                {!isNaN(gpas?.gpa) && gpas?.gpa}
              </Typography>
            </StyledTableBodyCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Box>
  )
}
